import { AlertCircle, RefreshCw, Settings2 } from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { AssignCustomBrebKeyDialog } from "../components/AssignCustomBrebKeyDialog";
import { GlobalFeaturesDrawer } from "../components/GlobalFeaturesDrawer";
import { OrganizationFeaturesDrawer } from "../components/OrganizationFeaturesDrawer";
import { OrganizationsAdminFilters } from "../components/OrganizationsAdminFilters";
import { OrganizationsAdminTable } from "../components/OrganizationsAdminTable";
import { type BrebKeyTransferTarget, TransferBrebKeyDialog } from "../components/TransferBrebKeyDialog";
import { ORGANIZATIONS_PATH, visibleFeatureCatalog } from "../constants/organizationsAdmin.constants";
import { useBrebCustomKeyAccess } from "../hooks/useBrebCustomKeyAccess";
import { useCardCopTopUpsAccess } from "../hooks/useCardCopTopUpsAccess";
import { useGlobalFeatures } from "../hooks/useGlobalFeatures";
import { useOrganizationOverview } from "../hooks/useOrganizationOverview";
import { useOrganizationsAdminList } from "../hooks/useOrganizationsAdminList";
import type {
  AdminOrganization,
  GlobalFeature,
  GlobalFeatureMode,
  OrganizationFeature,
} from "../types/organizationsAdmin.types";

// On for all says what each feature opens; the other two moves read the same for every feature.
const ON_FOR_ALL_TOAST_KEYS = {
  payout_batches_enabled: "toast_payout_batches_rollout_on",
  breb_receiving_key_enabled: "toast_rollout_on",
  otc_enabled: "toast_crypto_rollout_on",
  card_deposits_enabled: "toast_card_deposits_rollout_on",
  card_cop_top_ups_enabled: "toast_card_cop_top_ups_rollout_on",
  breb_qr_payments_enabled: "toast_breb_qr_payments_rollout_on",
} as const satisfies Record<GlobalFeature, string>;

/**
 * `/app/organizations` — INTERNAL staff directory of client organizations. Each row opens a side panel with
 * the organization's own switches; the switches that apply to every organization live behind "General
 * settings".
 */
const OrganizationsListPage = () => {
  const { t } = useTranslations("organizations");
  const { t: tGlobal } = useTranslations("global");
  const { can } = useAuth();
  const list = useOrganizationsAdminList();
  const global = useGlobalFeatures();
  const canAssignCustomKey = useBrebCustomKeyAccess();
  const canSeeCardCopTopUps = useCardCopTopUpsAccess();
  const catalog = useMemo(() => visibleFeatureCatalog(canSeeCardCopTopUps), [canSeeCardCopTopUps]);
  const [assigningKey, setAssigningKey] = useState<AdminOrganization | null>(null);
  const [transferringKey, setTransferringKey] = useState<BrebKeyTransferTarget | null>(null);
  const [configuring, setConfiguring] = useState<AdminOrganization | null>(null);
  const [isConfiguringOpen, setIsConfiguringOpen] = useState(false);
  const [isGlobalOpen, setIsGlobalOpen] = useState(false);

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_organizations"), to: ORGANIZATIONS_PATH },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const canWrite = can("organizations:write");
  // The row on screen carries the latest switch values; the snapshot only covers a row that left the page.
  const configured = configuring ? (list.rows.find((row) => row.id === configuring.id) ?? configuring) : null;
  const overview = useOrganizationOverview(configured?.id ?? null, isConfiguringOpen);

  const labelsInMode = (mode: GlobalFeatureMode) =>
    catalog
      .filter((definition) => definition.global && global.features?.[definition.global] === mode)
      .map((definition) => t(definition.labelKey));
  const forAllLabels = labelsInMode("on_for_all");
  const offForAllLabels = labelsInMode("off_for_all");

  const openConfiguration = (organization: AdminOrganization) => {
    setConfiguring(organization);
    setIsConfiguringOpen(true);
  };

  const applyFeature = async (
    organization: AdminOrganization,
    feature: OrganizationFeature,
    enabled: boolean,
  ) => {
    const definition = catalog.find((entry) => entry.feature === feature);
    const result = await list.toggleFeature(organization, feature, enabled);
    if (result === "failed") {
      colocaToast.error(t("toast_update_failed", { name: organization.name }));
      return;
    }
    if (definition) {
      colocaToast.success(
        t(enabled ? definition.toastOnKey : definition.toastOffKey, { name: organization.name }),
      );
    }
  };

  const applyGlobalFeature = async (feature: GlobalFeature, mode: GlobalFeatureMode) => {
    const result = await global.setFeature(feature, mode);
    if (result === "failed") {
      colocaToast.error(t("toast_rollout_failed"));
      return;
    }
    const definition = catalog.find((entry) => entry.global === feature);
    const name = definition ? t(definition.labelKey) : "";
    if (mode === "on_for_all") colocaToast.success(t(ON_FOR_ALL_TOAST_KEYS[feature]));
    if (mode === "per_organization")
      colocaToast.success(t("toast_rollout_per_organization", { feature: name }));
    if (mode === "off_for_all") colocaToast.success(t("toast_rollout_off_for_all", { feature: name }));
  };

  if (!list.hasAccess) return <NoAccessView />;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <ColocaButton
              variant="secondary"
              size="sm"
              icon={Settings2}
              onClick={() => setIsGlobalOpen(true)}
            >
              {t("action_global_settings")}
            </ColocaButton>
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("action_refresh")}
              className={cn(list.isLoading && "[&_svg]:animate-spin")}
              onClick={list.refresh}
            />
          </div>
        }
      />

      <OrganizationsAdminFilters
        search={list.search}
        onSearchChange={list.setSearch}
        searchTooShort={list.searchTooShort}
        status={list.status}
        onStatusChange={list.setStatus}
        payoutBatches={list.payoutBatches}
        onPayoutBatchesChange={list.setPayoutBatches}
      />

      {(forAllLabels.length > 0 || offForAllLabels.length > 0) && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          {offForAllLabels.length > 0 && (
            <span className="font-medium text-destructive">
              {t("features_off_for_all", { features: offForAllLabels.join(" · ") })}
            </span>
          )}
          {forAllLabels.length > 0 && (
            <span>{t("features_for_all", { features: forAllLabels.join(" · ") })}</span>
          )}
          <ColocaButton variant="link" size="xs" onClick={() => setIsGlobalOpen(true)}>
            {t("action_change")}
          </ColocaButton>
        </p>
      )}

      {list.hasError && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("error_load")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={list.refresh}>
                {t("action_retry")}
              </ColocaButton>
            </span>
          }
        />
      )}

      {!list.hasError && (
        <OrganizationsAdminTable
          rows={list.rows}
          isLoading={list.isLoading}
          page={list.page}
          total={list.total}
          onPageChange={list.setPage}
          onConfigure={openConfiguration}
          catalog={catalog}
          globalFeatures={global.features}
          hasActiveFilters={list.hasActiveFilters}
          onClearFilters={list.clearFilters}
        />
      )}

      <OrganizationFeaturesDrawer
        organization={configured}
        open={isConfiguringOpen}
        onOpenChange={setIsConfiguringOpen}
        overview={overview.state}
        onReloadOverview={overview.reload}
        catalog={catalog}
        globalFeatures={global.features}
        isPending={configured !== null && list.pendingIds.has(configured.id)}
        onFeatureChange={canWrite ? applyFeature : undefined}
        onAssignCustomKey={
          canAssignCustomKey
            ? (organization) => {
                setIsConfiguringOpen(false);
                setAssigningKey(organization);
              }
            : undefined
        }
      />

      <GlobalFeaturesDrawer
        open={isGlobalOpen}
        onOpenChange={setIsGlobalOpen}
        catalog={catalog}
        features={global.features}
        hasError={global.hasError}
        savingFeature={global.savingFeature}
        isBusy={global.isBusy}
        onChange={canWrite ? applyGlobalFeature : undefined}
        onRetry={global.reload}
      />

      <AssignCustomBrebKeyDialog
        organization={assigningKey}
        onOpenChange={(open) => !open && setAssigningKey(null)}
        onTransferKey={(key) => {
          if (!assigningKey) return;
          setTransferringKey({ organization: assigningKey, key });
          setAssigningKey(null);
        }}
      />

      <TransferBrebKeyDialog
        transfer={transferringKey}
        onOpenChange={(open) => !open && setTransferringKey(null)}
      />
    </div>
  );
};

export default OrganizationsListPage;
